import React from 'react';
import { Alert, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { useHeaderHeight } from '@react-navigation/elements';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp, createNativeStackNavigator } from '@react-navigation/native-stack';

import { ClearIcon } from '@/assets/icons/clearIcon';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { NativeCheckoutHeader } from '@/feature/devsettings/components/NativeCheckoutHeader';

type InnerParams = {
    CheckoutLevel: { level: number };
};

type InnerNav = NativeStackNavigationProp<InnerParams>;

const InnerStack = createNativeStackNavigator<InnerParams>();

const FAKE_PICKS = [
    { player: 'LeBron James', amount: '$5.00' },
    { player: 'Steph Curry', amount: '$5.00' },
    { player: 'Kevin Durant', amount: '$5.00' },
    { player: 'Nikola Jokic', amount: '$5.00' },
    { player: 'Luka Doncic', amount: '$5.00' },
    { player: 'Jayson Tatum', amount: '$5.00' },
    { player: 'Giannis Antetokounmpo', amount: '$5.00' },
    { player: 'Joel Embiid', amount: '$5.00' },
    { player: 'Anthony Davis', amount: '$5.00' },
    { player: 'Devin Booker', amount: '$5.00' },
    { player: 'Donovan Mitchell', amount: '$5.00' },
    { player: 'Damian Lillard', amount: '$5.00' },
];

const FakeBalanceButton = ({ onPress }: { onPress: () => void }) => (
    <Pressable onPress={onPress} hitSlop={12}>
        <View style={styles.balancePill}>
            <Text variant="labelMedium" color={ds.colorToken.text.success}>
                $420.69
            </Text>
            <View style={styles.separator} />
            <Text variant="labelMedium" color={ds.colorToken.text.primary}>
                +
            </Text>
        </View>
    </Pressable>
);

const CheckoutLevelScreen = () => {
    const navigation = useNavigation<InnerNav>();
    const route = useRoute<RouteProp<InnerParams, 'CheckoutLevel'>>();
    const { level } = route.params;
    const headerHeight = useHeaderHeight();

    const onTrashPress = () =>
        Alert.alert('Clear picks?', 'This would call removeAllPicks()', [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Clear', style: 'destructive' },
        ]);

    const onBalancePress = () => Alert.alert('Payout breakdown', 'This would open the payout detail sheet');

    return (
        <ScrollView style={styles.scrollView} contentContainerStyle={[styles.scroll, { paddingTop: headerHeight + 8 }]}>
            <NativeCheckoutHeader
                balanceButton={<FakeBalanceButton onPress={onBalancePress} />}
                rightItem={<ClearIcon color={ds.colorValue.icon.danger} />}
                onRightItemPress={onTrashPress}
                onClose={() => navigation.getParent()?.goBack()}
            />

            {level === 1 ? (
                <Box
                    mb="s16"
                    padding="s12"
                    borderRadius="r8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                >
                    <Text variant="labelSmall" color="neutral400" mb="s4">
                        What this shows
                    </Text>
                    <Text variant="bodySmaller" color="neutral300">
                        A native stack header wired dynamically via setOptions. Three slots: close (X) on the left,
                        balance pill in the center, action button on the right. Push deeper to see the OS animate the
                        header buttons — on iOS 26 they appear inside a liquid glass pill.
                    </Text>
                </Box>
            ) : null}

            <Box gap="s8" mb="s24">
                <Button
                    label={`Push level ${level + 1}`}
                    onPress={() => navigation.push('CheckoutLevel', { level: level + 1 })}
                />
                {level > 1 ? <Button label="Go back" onPress={() => navigation.goBack()} /> : null}
            </Box>

            <Text variant="labelSmall" color="neutral400" mb="s12">
                Fake picks (placeholder content — no real logic)
            </Text>

            {FAKE_PICKS.map(pick => (
                <Box
                    key={pick.player}
                    padding="s16"
                    borderRadius="r8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    mb="s8"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Text variant="bodySmall" color={ds.colorToken.text.primary}>
                        {pick.player}
                    </Text>
                    <Text variant="labelSmall" color="neutral300">
                        {pick.amount}
                    </Text>
                </Box>
            ))}

            <Box mt="s16" padding="s12" borderRadius="r8" backgroundColor={ds.colorToken.background.neutral.subtler}>
                <Text variant="labelSmall" color="neutral400" mb="s4">
                    Stack depth: {level}
                </Text>
                <Text variant="bodySmaller" color="neutral300">
                    The X button always dismisses the entire modal regardless of how deep the stack goes.
                </Text>
            </Box>
        </ScrollView>
    );
};

export const NativeHeaderCheckoutDemoScreen = () => (
    <InnerStack.Navigator
        initialRouteName="CheckoutLevel"
        screenOptions={{
            headerShown: true,
            headerTransparent: true,
            headerTintColor: ds.colorValue.text.primary,
            scrollEdgeEffects: { top: 'soft', bottom: 'automatic', left: 'automatic', right: 'automatic' },
        }}
    >
        <InnerStack.Screen
            name="CheckoutLevel"
            component={CheckoutLevelScreen}
            initialParams={{ level: 1 }}
            options={{ title: 'Checkout Header' }}
        />
    </InnerStack.Navigator>
);

const styles = StyleSheet.create({
    scrollView: {
        flex: 1,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
    scroll: {
        padding: 16,
        paddingBottom: 48,
    },
    balancePill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderRadius: 24,
        paddingHorizontal: 16,
        paddingVertical: 8,
        gap: 8,
    },
    separator: {
        width: 1,
        height: 16,
        backgroundColor: ds.colorValue.border.strong,
    },
});
